import { useMemo, useState } from 'react'
import { useMyMedias } from '@/hooks/queries'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { Media } from '@/models/media'
import { SkeletonLine } from './ui/Skeleton'

interface MediaLibraryProps {
  onClose?: () => void
  onSelect?: (media: Media) => void
}

/**
 * Port of components/MediaLibrary.vue.
 *
 * The original rendered four placeholder tiles from `media: [1,2,3,4]`, all
 * labelled "spawn-world.png". It is wired to the real /me/medias list here,
 * along with the search box that was previously inert.
 */
export function MediaLibrary({ onClose, onSelect }: MediaLibraryProps) {
  const { data: medias = [], isPending } = useMyMedias()
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return medias
    return medias.filter((media) =>
      (media.tags ?? []).some((tag) => tag.toLowerCase().includes(needle)),
    )
  }, [medias, query])

  const confirm = () => {
    const selected = medias.find((media) => media.id === selectedId)
    if (selected) onSelect?.(selected)
    onClose?.()
  }

  return (
    <div className="media-library">
      <div className="header-section">
        <div className="title-section">Media Library</div>
        <div className="search-section">
          <input
            placeholder="Search media"
            aria-label="Search media"
            className="search-box"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      <div className="content-section">
        <div className="media-section">
          <div className="media-list">
            {isPending
              ? Array.from({ length: 4 }, (_, index) => (
                  <div className="media-list-item is-skeleton" key={index} aria-hidden="true">
                    <div className="media-list-item-section-image skeleton" />
                    <div className="media-list-item-section-name">
                      <SkeletonLine width="4.5em" />
                    </div>
                  </div>
                ))
              : null}

            {filtered.map((media) => (
              <button
                type="button"
                key={media.id}
                className={cn('media-list-item', media.id === selectedId && 'selected')}
                aria-pressed={media.id === selectedId}
                onClick={() => setSelectedId(media.id)}
              >
                <div className="media-list-item-section-image" style={backgroundImage(media.src)} />
                <div className="media-list-item-section-name">
                  {media.tags?.[0] ?? `#${media.id}`}
                </div>
              </button>
            ))}

            {!isPending && filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-title">No media found</div>
                <div className="empty-state-body">
                  {query ? 'Nothing matched that search.' : 'Upload an image to get started.'}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="footer-section">
        <div className="confirm-section">
          <button type="button" className="confirm-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="confirm-btn"
            onClick={confirm}
            disabled={selectedId === null}
          >
            Select
          </button>
        </div>
      </div>
    </div>
  )
}
